// Off-screen брендированная карточка для шеринга графика как PNG.
// Рендерится постоянно за пределами вьюпорта (left:-9999px), чтобы recharts успел
// отрисоваться к моменту тапа «Поделиться» (D2/D4). html2canvas снимает node по ref.
// Source of truth: docs/specs/sprint-6-sharing/stage-1-contracts.md §2 (D7/D8).

import { forwardRef } from 'react';
import { Activity } from 'lucide-react';
import { BiomarkerChart } from '@/components/dashboard/BiomarkerChart';
import { formatRuDate } from '@/lib/chart-data';
import { capitalizeFirst, cn, formatNumber } from '@/lib/utils';
import { displayTier, DOT_BG_BY_TIER, VALUE_TEXT_BY_TIER } from '@/lib/biomarkerStatus';
import type {
  BiomarkerDetailCurrent,
  BiomarkerDetailMeta,
  BiomarkerDetailPoint,
  ClinicalStatus,
  TimelinePeriod,
} from '@/api/types';

// Лейбл — по clinical_status (🟠 = «В норме», как на детали); цвет — по тиру.
const STATUS_LABEL: Record<ClinicalStatus, string> = {
  in_range: 'В норме',
  out_of_range: 'Вне нормы',
  no_range: 'Без оценки',
};

const PERIOD_LABEL: Record<TimelinePeriod, string> = {
  '3m': 'за 3 месяца',
  '6m': 'за 6 месяцев',
  '1y': 'за год',
  all: 'за всё время',
};

type Props = {
  meta: BiomarkerDetailMeta;
  points: BiomarkerDetailPoint[];
  current: BiomarkerDetailCurrent | null;
  period: TimelinePeriod;
};

// Фикс CSS-ширина ~360px; html2canvas scale:3 даёт ~1080px ретина-PNG (D3).
export const ShareCard = forwardRef<HTMLDivElement, Props>(function ShareCard(
  { meta, points, current, period },
  ref,
) {
  const latest = current
    ? formatRuDate(current.measured_at)
    : points.length
      ? formatRuDate(points[points.length - 1].measured_at)
      : '';

  return (
    <div
      ref={ref}
      style={{ width: 360 }}
      className="flex flex-col gap-3 bg-white p-5 font-sans text-gray-900"
    >
      {/* brand */}
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Activity className="h-4 w-4" strokeWidth={2.5} />
        </div>
        <span className="text-base font-bold tracking-tight text-primary">HealthLab</span>
        <span className="ml-auto text-[11px] text-gray-400">{PERIOD_LABEL[period]}</span>
      </div>

      {/* marker name */}
      <div>
        <div className="text-lg font-bold leading-tight">{capitalizeFirst(meta.canonical_name_ru)}</div>
        {meta.description_short && (
          <div className="text-[11px] text-gray-500">{meta.description_short}</div>
        )}
      </div>

      {/* current value */}
      {current && (() => {
        const tier = displayTier(current.clinical_status, meta.optimum_status);
        return (
        <div>
          <div className="flex items-baseline gap-2">
            {/* HEAL-33: качественный результат — словом. «0» на картинке, которой
                человек делится, выглядел бы как отсутствующий результат. */}
            <span
              className={cn(
                'font-bold',
                current.value_qualitative ? 'text-xl' : 'font-mono text-3xl',
                VALUE_TEXT_BY_TIER[tier],
              )}
            >
              {current.value_qualitative
                ? capitalizeFirst(current.value_qualitative)
                : formatNumber(current.value_normalized)}
            </span>
            {!current.value_qualitative && (
              <span className="text-sm text-gray-600">{current.unit_normalized}</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[12px] text-gray-600">
            <span className={cn('h-2 w-2 rounded-full', DOT_BG_BY_TIER[tier])} />
            <span>
              <strong className="text-gray-900">{STATUS_LABEL[current.clinical_status]}.</strong>{' '}
              Диапазон: {meta.normal_range_label}
            </span>
          </div>
        </div>
        );
      })()}

      {/* chart */}
      <BiomarkerChart
        points={points}
        normalMin={meta.normal_min}
        normalMax={meta.normal_max}
        unit={meta.standard_unit}
        period={period}
      />

      {/* footer: disclaimer + meta */}
      <div className="border-t border-gray-100 pt-2 text-[10px] leading-snug text-gray-400">
        <div>Личный health-трекер, не медицинский документ.</div>
        <div className="mt-0.5 flex justify-between">
          <span>app.healthlab.kz</span>
          {latest && <span>данные на {latest}</span>}
        </div>
      </div>
    </div>
  );
});